import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Divider,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Avatar,
  LinearProgress,
  AppBar,
  Toolbar,
  Stack,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Chip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Skeleton,
  InputAdornment,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Zoom,
  Slide,
  Collapse,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  PersonOutline as PersonIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  WorkOutline as JobIcon,
  CloudUpload,
  Description,
  CameraAlt,
  SendOutlined as SendIcon,
  CheckCircleOutline as SuccessIcon,
  Delete,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Article as ArticleIcon,
  Group as GroupIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  Visibility as VisibilityIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Language as WebsiteIcon,
  Mail as MailIcon,
  CheckCircle,
  DragIndicator as DragIcon,
  Language as LanguageIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import axios from 'axios';
import candidateTheme from '../../theme/candidateTheme';
import { translations } from '../../utils/translations';
import { API_ENDPOINTS } from '../../config/api';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const PublicJobApplication = () => {
  // Language state
  const [language, setLanguage] = useState('en');
  const t = translations[language];
  const isRTL = language === 'ar';

  // Create dynamic theme with RTL support
  const theme = createTheme({
    ...candidateTheme,
    direction: isRTL ? 'rtl' : 'ltr',
  });

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [applicationId, setApplicationId] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    full_name: '',
    phone: '',
    email: '',
    city: '',
    address: '',
    nationality: '',
    marital_status: '',

    // Job Selection
    job_id: '',

    // How They Found Us
    how_heard: '',
    how_heard_other: '',
    referral_name: '',
    message: '',

    // Professional Links (optional based on job)
    linkedin_url: '',
    portfolio_url: '',
    github_url: '',

    // Professional Information (optional based on job)
    years_of_experience: '',
    education_level: '',
    field_of_study: '',
    university_name: '',
    graduation_year: '',
    current_job_title: '',
    current_company: '',
    expected_salary: '',
    notice_period: '',
  });

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});

  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvUploadProgress, setCvUploadProgress] = useState(0);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);

  const [consentAgreed, setConsentAgreed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // UI State
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const cvInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  const howHeardOptions = [
    { value: 'Facebook', label: t.facebook, icon: <FacebookIcon /> },
    { value: 'LinkedIn', label: t.linkedin, icon: <LinkedInIcon /> },
    { value: 'Friend', label: t.friend, icon: <GroupIcon /> },
    { value: 'Website', label: t.website, icon: <WebsiteIcon /> },
    { value: 'Job Board', label: t.jobBoard, icon: <ArticleIcon /> },
    { value: 'Other', label: t.other, icon: <InfoIcon /> },
  ];

  useEffect(() => {
    fetchJobs();

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Cleanup on unmount
    return () => {
      clearInterval(timer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle id from URL parameter
  useEffect(() => {
    if (jobs.length === 0) return; // Wait for jobs to load

    const urlParams = new URLSearchParams(window.location.search);
    const idFromUrl = urlParams.get('id') || urlParams.get('job_id');

    if (!idFromUrl) return; // No ID in URL

    // Try to find by job_id (string like "ZON_JOB_001") or by matching number in job_id
    const job = jobs.find(j => {
      const jobId = String(j.job_id).toUpperCase();
      const searchId = String(idFromUrl).toUpperCase();

      // Match strategies:
      // 1. Exact match: "ZON_JOB_001" === "ZON_JOB_001"
      if (jobId === searchId) return true;

      // 2. Match with padding: id=1 matches ZON_JOB_001
      const paddedId = 'ZON_JOB_' + searchId.padStart(3, '0');
      if (jobId === paddedId) return true;

      // 3. Match number at end: id=1 matches any job ending with 001
      if (jobId.endsWith('_' + searchId.padStart(3, '0'))) return true;

      // 4. Match database id if exists
      if (j.id && String(j.id) === searchId) return true;

      return false;
    });

    if (job) {
      setSelectedJob(job);
      setFormData(prev => ({ ...prev, job_id: job.job_id }));

      // Scroll to form after a short delay
      setTimeout(() => {
        const formElement = document.getElementById('application-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      console.warn('Job not found for ID:', idFromUrl, 'Available jobs:', jobs.map(j => j.job_id));
    }
  }, [jobs]);

  const fetchJobs = async () => {
    try {
      setLoadError(false);
      abortControllerRef.current = new AbortController();

      const response = await axios.get(API_ENDPOINTS.JOBS, {
        signal: abortControllerRef.current.signal,
      });

      setJobs(response.data.jobs);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        setLoadError(true);
        showSnackbar(t.failedToLoad, 'error');
        console.error('Error fetching jobs:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[0-9]{10,15}$/;
    return re.test(phone.replace(/\s/g, ''));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: '' });
    }

    // Real-time validation
    if (name === 'email' && value) {
      if (!validateEmail(value)) {
        setValidationErrors({ ...validationErrors, email: 'Please enter a valid email address' });
      }
    }

    if (name === 'phone' && value) {
      if (!validatePhone(value)) {
        setValidationErrors({ ...validationErrors, phone: 'Please enter a valid phone number (10-15 digits)' });
      }
    }

    // Update selected job details
    if (name === 'job_id' && value) {
      const job = jobs.find(j => j.job_id === value);
      setSelectedJob(job);
    }
  };

  // Drag and Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      if (type === 'cv') {
        validateAndUploadCV(file);
      } else if (type === 'profile') {
        validateAndUploadProfile(file);
      }
    }
  };

  const validateAndUploadCV = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar(`${t.fileTooLarge} 5MB`, 'error');
      return;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar(`${t.invalidFileType} PDF, DOC, DOCX`, 'error');
      return;
    }

    // Simulate upload with progress
    setCvUploading(true);
    setCvUploadProgress(0);

    const interval = setInterval(() => {
      setCvUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setCvUploading(false);
          setCvFile(file);
          showSnackbar(t.cvSuccess, 'success');
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const handleCvChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    validateAndUploadCV(file);
  };

  const validateAndUploadProfile = (file) => {
    if (file.size > 2 * 1024 * 1024) {
      showSnackbar(`${t.fileTooLarge} 2MB`, 'error');
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar(`${t.invalidFileType} JPG, PNG, WEBP`, 'error');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
    setProfilePicture(file);
    showSnackbar(t.photoSuccess, 'success');
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    validateAndUploadProfile(file);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Validation
    const errors = {};
    if (!formData.full_name) errors.full_name = t.required;
    if (!formData.email) {
      errors.email = t.required;
    } else if (!validateEmail(formData.email)) {
      errors.email = t.invalidEmail;
    }
    if (!formData.phone) {
      errors.phone = t.required;
    } else if (!validatePhone(formData.phone)) {
      errors.phone = t.invalidPhone;
    }
    if (!formData.job_id) errors.job_id = t.selectPosition;
    if (!formData.how_heard) errors.how_heard = t.required;
    if (formData.how_heard === 'Other' && !formData.how_heard_other) {
      errors.how_heard_other = t.required;
    }
    if (formData.how_heard === 'Friend' && !formData.referral_name) {
      errors.referral_name = t.required;
    }
    if (!cvFile) {
      showSnackbar(t.uploadCVRequired, 'error');
      setSubmitting(false);
      return;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showSnackbar(t.fillRequired, 'error');
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!consentAgreed) {
      showSnackbar(t.mustAgreeConsent, 'error');
      setSubmitting(false);
      return;
    }

    try {
      const submitData = new FormData();

      // Basic Information (Required)
      submitData.append('full_name', formData.full_name);
      submitData.append('mobile', formData.phone);
      submitData.append('email', formData.email);
      submitData.append('city', formData.city);
      submitData.append('nationality', formData.nationality);

      // Optional basic fields
      if (formData.address) submitData.append('address', formData.address);
      if (formData.marital_status) submitData.append('marital_status', formData.marital_status);

      // Job Selection
      submitData.append('job_id', formData.job_id);

      // Files (Required)
      submitData.append('cv', cvFile);
      if (profilePicture) {
        submitData.append('profile_picture', profilePicture);
      }

      // How They Found Us (Required)
      submitData.append('how_heard', formData.how_heard);
      if (formData.how_heard_other) {
        submitData.append('how_heard_other', formData.how_heard_other);
      }
      if (formData.referral_name) {
        submitData.append('referral_name', formData.referral_name);
      }
      if (formData.message) {
        submitData.append('message', formData.message);
      }

      // Professional Links (Optional)
      if (formData.linkedin_url) submitData.append('linkedin_url', formData.linkedin_url);
      if (formData.portfolio_url) submitData.append('portfolio_url', formData.portfolio_url);
      if (formData.github_url) submitData.append('github_url', formData.github_url);

      // Professional Information (Optional, based on job requirements)
      if (formData.years_of_experience) submitData.append('years_of_experience', formData.years_of_experience);
      if (formData.education_level) submitData.append('education_level', formData.education_level);
      if (formData.field_of_study) submitData.append('field_of_study', formData.field_of_study);
      if (formData.university_name) submitData.append('university_name', formData.university_name);
      if (formData.graduation_year) submitData.append('graduation_year', formData.graduation_year);
      if (formData.current_job_title) submitData.append('current_job_title', formData.current_job_title);
      if (formData.current_company) submitData.append('current_company', formData.current_company);
      if (formData.expected_salary) submitData.append('expected_salary', formData.expected_salary);
      if (formData.notice_period) submitData.append('notice_period', formData.notice_period);

      const response = await axios.post(API_ENDPOINTS.APPLY, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setApplicationId(response.data.application_id || `APP-${Date.now()}`);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err.response?.data?.errors) {
        // إذا كانت الأخطاء مصفوفة (الرسائل العربية من Backend)
        if (Array.isArray(err.response.data.errors)) {
          const arabicErrors = err.response.data.errors;

          // عرض كل الأخطاء في رسالة واحدة
          const errorMessage = arabicErrors.join('\n');
          showSnackbar(errorMessage, 'error');

          // محاولة map الأخطاء للحقول
          const mappedErrors = {};
          const validationErrors = err.response.data.validation_errors || {};
          Object.keys(validationErrors).forEach(key => {
            if (validationErrors[key] && validationErrors[key].length > 0) {
              mappedErrors[key] = validationErrors[key][0];
            }
          });

          if (Object.keys(mappedErrors).length > 0) {
            setValidationErrors(mappedErrors);

            // Scroll to first error field
            setTimeout(() => {
              const firstErrorField = Object.keys(mappedErrors)[0];
              const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
              }
            }, 100);
          }
        }
        // إذا كانت الأخطاء object (الطريقة القديمة)
        else {
          const serverErrors = err.response.data.errors;
          const mappedErrors = {};

          // Map server errors to form fields
          Object.keys(serverErrors).forEach(key => {
            if (serverErrors[key] && serverErrors[key].length > 0) {
              mappedErrors[key] = serverErrors[key][0];
            }
          });

          setValidationErrors(mappedErrors);

          // Show first error message instead of generic message
          const firstError = Object.values(mappedErrors)[0] || err.response.data.message || t.fillRequired;
          showSnackbar(firstError, 'error');

          // Scroll to first error field
          setTimeout(() => {
            const firstErrorField = Object.keys(mappedErrors)[0];
            const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              errorElement.focus();
            }
          }, 100);
        }
      } else {
        showSnackbar(err.response?.data?.message || 'Failed to submit application. Please try again.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage;
    }
  };

  // Don't show full error screen - let the form load and show error in the job select field

  // Success Screen
  if (submitted) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          dir={isRTL ? 'rtl' : 'ltr'}
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Confetti-like background elements */}
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              left: '10%',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#29204C',
              animation: `${float} 3s ease-in-out infinite`,
              opacity: 0.2,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '20%',
              right: '15%',
              width: 15,
              height: 15,
              borderRadius: '50%',
              background: '#68B977',
              animation: `${float} 4s ease-in-out infinite`,
              opacity: 0.3,
            }}
          />

          <Zoom in timeout={600}>
            <Paper
              elevation={6}
              sx={{
                maxWidth: 700,
                width: '100%',
                p: { xs: 4, sm: 6 },
                textAlign: 'center',
                borderRadius: 5,
                background: 'white',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: '0 10px 30px rgba(41, 32, 76, 0.4)',
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              >
                <CheckCircle sx={{ fontSize: 60, color: 'white' }} />
              </Box>

              <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: '#29204C', mb: 3, fontSize: { xs: '1.75rem', sm: '2.5rem' } }}>
                {t.applicationSubmitted}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.9, fontSize: '1.1rem' }}>
                {t.thankYou} <strong>{t.companyName}</strong>. {t.applicationReceived}
              </Typography>

              <Box sx={{ mb: 4, p: 3, background: '#f8f9fa', borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#29204C' }}>
                  {t.whatNext}
                </Typography>
                <Stack spacing={2} alignItems="flex-start" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: '#29204C',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                      }}
                    >
                      1
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{t.applicationReview}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.applicationReviewTime}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: '#29204C',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      2
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{t.initialScreening}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.initialScreeningDesc}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: '#29204C',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      3
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{t.finalDecision}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.finalDecisionDesc}</Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => window.location.reload()}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                    borderRadius: 3,
                    textTransform: 'none',
                    minHeight: 44,
                  }}
                >
                  Close
                </Button>
              </Stack>
            </Paper>
          </Zoom>
        </Box>
      </ThemeProvider>
    );
  }

  // Loading Screen
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          dir={isRTL ? 'rtl' : 'ltr'}
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 3, color: '#29204C' }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              {t.loadingPositions}
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        dir={isRTL ? 'rtl' : 'ltr'}
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          position: 'relative',
        }}
      >
        {/* Enhanced Professional Header */}
        <AppBar
          position="sticky"
          elevation={3}
          sx={{
            background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
            backdropFilter: 'blur(10px)',
            borderBottom: '3px solid rgba(104, 185, 119, 0.3)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 4 }, py: 1.5 }}>
            {/* Logo Section */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <img
                src="/zonnetLogo.png"
                alt="ZonNet Logo"
                style={{
                  height: '50px',
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, lineHeight: 1.2 }}>
                  {t.companyName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {t.careerOpportunities}
                </Typography>
              </Box>
            </Stack>

            {/* Desktop Navigation */}
            <Stack direction="row" spacing={3} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {/* Language Toggle - Hidden but code kept for future use */}
              {false && (
                <ToggleButtonGroup
                  value={language}
                  exclusive
                  onChange={handleLanguageChange}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      px: 2,
                      py: 0.5,
                      fontWeight: 600,
                      '&.Mui-selected': {
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.3)',
                        }
                      },
                      '&:hover': {
                        background: 'rgba(255,255,255,0.1)',
                      }
                    }
                  }}
                >
                  <ToggleButton value="en">EN</ToggleButton>
                  <ToggleButton value="ar">عربي</ToggleButton>
                </ToggleButtonGroup>
              )}
              {/* Home Button - Hidden but code kept for future use */}
              {false && (
                <Button
                  color="inherit"
                  startIcon={<HomeIcon />}
                  onClick={() => window.location.href = '/'}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {t.home}
                </Button>
              )}
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', fontWeight: 500 }}>
                  {currentTime.toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontSize: '0.95rem', fontWeight: 600 }}>
                  {currentTime.toLocaleTimeString(isRTL ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </Typography>
              </Box>
            </Stack>

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              sx={{ display: { xs: 'flex', md: 'none' } }}
              onClick={() => setMobileMenuOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 250,
              background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
              color: 'white',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700}>{t.menu}</Typography>
              <IconButton color="inherit" onClick={() => setMobileMenuOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <List>
              {/* Hidden for now but kept for future use */}
              {false && (
                <ListItemButton onClick={() => window.location.href = '/'}>
                  <ListItemIcon sx={{ color: 'white' }}>
                    <HomeIcon />
                  </ListItemIcon>
                  <ListItemText primary={t.home} />
                </ListItemButton>
              )}
              {false && (
                <ListItem sx={{ mt: 2 }}>
                  <ToggleButtonGroup
                    value={language}
                    exclusive
                    onChange={handleLanguageChange}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiToggleButton-root': {
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.3)',
                        fontWeight: 600,
                        '&.Mui-selected': {
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                        }
                      }
                    }}
                  >
                    <ToggleButton value="en">EN</ToggleButton>
                    <ToggleButton value="ar">عربي</ToggleButton>
                  </ToggleButtonGroup>
                </ListItem>
              )}
              <ListItem>
                <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ textAlign: 'center', width: '100%' }}>
                  Career Application Form
                </Typography>
              </ListItem>
            </List>
          </Box>
        </Drawer>

        <Container maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
          {/* Enhanced Hero Section with Statistics */}
          <Fade in timeout={800}>
            <Box sx={{ mb: 5 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: '#29204C',
                    mb: 2,
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    animation: `${slideIn} 0.8s ease-out`,
                  }}
                >
                  {t.heroTitle}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    maxWidth: 700,
                    mx: 'auto',
                    lineHeight: 1.8,
                    fontWeight: 400,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  }}
                >
                  {t.heroSubtitle}
                </Typography>
              </Box>
            </Box>
          </Fade>

          {/* Application Form */}
          <Slide in direction="up" timeout={600}>
            <Card
              elevation={4}
              sx={{
                borderRadius: 4,
                overflow: 'visible',
                background: 'white',
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Box component="form" onSubmit={handleSubmit}>
                  {/* ========== PROFILE SECTION ========== */}
                  <Box sx={{ mb: 4 }}>
                    {/* Section Header */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: '#29204C',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 22, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" sx={{ color: '#29204C', fontWeight: 700 }}>
                        {t.personalInfo}
                      </Typography>
                    </Stack>

                    {/* Profile Picture - Centered */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                      <Box
                        onClick={() => profileInputRef.current?.click()}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          '&:hover .upload-overlay': {
                            opacity: 1,
                          }
                        }}
                      >
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            profilePicturePreview ? (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProfilePicture(null);
                                  setProfilePicturePreview(null);
                                }}
                                sx={{
                                  background: '#f44336',
                                  color: 'white',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    background: '#d32f2f',
                                  },
                                }}
                              >
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            ) : null
                          }
                        >
                          <Avatar
                            src={profilePicturePreview}
                            sx={{
                              width: 90,
                              height: 90,
                              border: '3px solid',
                              borderColor: profilePicturePreview ? '#29204C' : '#e0e0e0',
                              boxShadow: profilePicturePreview ? '0 2px 8px rgba(41, 32, 76, 0.2)' : '0 1px 4px rgba(0,0,0,0.1)',
                              transition: 'all 0.3s',
                            }}
                          >
                            <CameraAlt sx={{ fontSize: 32, color: '#999' }} />
                          </Avatar>
                        </Badge>
                        <Box
                          className="upload-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: '50%',
                            background: 'rgba(41, 32, 76, 0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: profilePicturePreview ? 0 : 0.7,
                            transition: 'opacity 0.3s',
                          }}
                        >
                          <CloudUpload sx={{ fontSize: 24, color: 'white' }} />
                        </Box>
                        <input
                          ref={profileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleProfilePictureChange}
                          style={{ display: 'none' }}
                        />
                      </Box>
                      <Typography variant="caption" display="block" textAlign="center" color="text.secondary" sx={{ mt: 1.5 }}>
                        {t.profilePicture} • Max 2MB
                      </Typography>
                    </Box>

                    {/* Personal Information Fields */}
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          label={t.fullName}
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          placeholder={t.fullNamePlaceholder}
                          error={!!validationErrors.full_name}
                          helperText={validationErrors.full_name}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon sx={{ color: '#29204C' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                              '&:hover fieldset': { borderColor: '#29204C' },
                              '&.Mui-focused fieldset': { borderColor: '#29204C' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t.email}
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder={t.emailPlaceholder}
                          error={!!validationErrors.email}
                          helperText={validationErrors.email || t.emailHelper}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: '#29204C' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                              '&:hover fieldset': { borderColor: '#29204C' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t.phone}
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder={t.phonePlaceholder}
                          error={!!validationErrors.phone}
                          helperText={validationErrors.phone || t.phoneHelper}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon sx={{ color: '#29204C' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                              '&:hover fieldset': { borderColor: '#29204C' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="City"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Enter your city"
                          error={!!validationErrors.city}
                          helperText={validationErrors.city}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                              '&:hover fieldset': { borderColor: '#29204C' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Nationality"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleInputChange}
                          placeholder="Enter your nationality"
                          error={!!validationErrors.nationality}
                          helperText={validationErrors.nationality}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                              '&:hover fieldset': { borderColor: '#29204C' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Enter your full address"
                          error={!!validationErrors.address}
                          helperText={validationErrors.address}
                          multiline
                          rows={2}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                              '&:hover fieldset': { borderColor: '#29204C' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth sx={{ minWidth: '100%' }} error={!!validationErrors.marital_status}>
                          <InputLabel shrink>Marital Status</InputLabel>
                          <Select
                            name="marital_status"
                            value={formData.marital_status}
                            onChange={handleInputChange}
                            displayEmpty
                            sx={{
                              borderRadius: 2,
                              width: '100%',
                              '& fieldset': {
                                borderColor: 'rgba(0,0,0,0.12)',
                                borderWidth: 2,
                              },
                              '&:hover fieldset': {
                                borderColor: '#29204C',
                              },
                            }}
                          >
                            <MenuItem value="">
                              <Typography color="text.secondary">Please select...</Typography>
                            </MenuItem>
                            <MenuItem value="single">Single</MenuItem>
                            <MenuItem value="married">Married</MenuItem>
                            <MenuItem value="divorced">Divorced</MenuItem>
                            <MenuItem value="widowed">Widowed</MenuItem>
                            <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                          </Select>
                          {validationErrors.marital_status && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {validationErrors.marital_status}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* ========== POSITION & SOURCE SECTION ========== */}
                  <Box sx={{ mb: 4 }}>
                    {/* Section Header */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: '#29204C',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <JobIcon sx={{ fontSize: 22, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" sx={{ color: '#29204C', fontWeight: 700 }}>
                        {t.positionSource}
                      </Typography>
                    </Stack>

                    <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormControl
                              fullWidth
                              required
                              error={!!validationErrors.job_id}
                              sx={{ minWidth: '100%' }}
                            >
                              <InputLabel shrink>{t.positionAppliedFor}</InputLabel>
                              <Select
                                name="job_id"
                                value={formData.job_id}
                                onChange={handleInputChange}
                                displayEmpty
                                disabled={jobs.length === 0}
                                sx={{
                                  borderRadius: 2,
                                  width: '100%',
                                  '& fieldset': {
                                    borderColor: 'rgba(0,0,0,0.12)',
                                    borderWidth: 2,
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#29204C',
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>
                                  <Typography color="text.secondary">Please select from the following...</Typography>
                                </MenuItem>
                                {jobs.length === 0 ? (
                                  <MenuItem disabled>
                                    <Box sx={{ py: 1.5, textAlign: 'center', width: '100%' }}>
                                      <InfoIcon sx={{ fontSize: 32, color: '#757575', mb: 0.5 }} />
                                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        No positions are currently available
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Please check back later for new opportunities
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ) : (
                                  jobs.map((job) => (
                                    <MenuItem key={job.job_id} value={job.job_id}>
                                      <Box>
                                        <Typography variant="body1" fontWeight={600}>
                                          {job.job_title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {job.location && `${job.location} • `}
                                          {job.job_type}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                              {validationErrors.job_id && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                  {validationErrors.job_id}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl
                              fullWidth
                              required
                              error={!!validationErrors.how_heard}
                              sx={{ minWidth: '100%' }}
                            >
                              <InputLabel shrink>{t.howHeard}</InputLabel>
                              <Select
                                name="how_heard"
                                value={formData.how_heard}
                                onChange={handleInputChange}
                                displayEmpty
                                sx={{
                                  borderRadius: 2,
                                  width: '100%',
                                  '& fieldset': {
                                    borderColor: 'rgba(0,0,0,0.12)',
                                    borderWidth: 2,
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#29204C',
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>
                                  <Typography color="text.secondary">Please select from the following...</Typography>
                                </MenuItem>
                                {howHeardOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                      {option.icon}
                                      <Typography>{option.label}</Typography>
                                    </Stack>
                                  </MenuItem>
                                ))}
                              </Select>
                              {validationErrors.how_heard && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                  {validationErrors.how_heard}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>

                          {formData.how_heard === 'Other' && (
                            <Grid item xs={12}>
                              <Collapse in={formData.how_heard === 'Other'}>
                                <TextField
                                  fullWidth
                                  required
                                  label={t.pleaseSpecify}
                                  name="how_heard_other"
                                  value={formData.how_heard_other}
                                  onChange={handleInputChange}
                                  placeholder={t.pleaseSpecifyPlaceholder}
                                  error={!!validationErrors.how_heard_other}
                                  helperText={validationErrors.how_heard_other}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      '& fieldset': {
                                        borderWidth: 2,
                                      },
                                    },
                                  }}
                                />
                              </Collapse>
                            </Grid>
                          )}

                          {formData.how_heard === 'Friend' && (
                            <Grid item xs={12}>
                              <Collapse in={formData.how_heard === 'Friend'}>
                                <TextField
                                  fullWidth
                                  required
                                  label={t.referralName}
                                  name="referral_name"
                                  value={formData.referral_name}
                                  onChange={handleInputChange}
                                  placeholder={t.referralNamePlaceholder}
                                  error={!!validationErrors.referral_name}
                                  helperText={validationErrors.referral_name || t.referralNameHelper}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <GroupIcon sx={{ color: '#29204C' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      '& fieldset': {
                                        borderWidth: 2,
                                      },
                                    },
                                  }}
                                />
                              </Collapse>
                            </Grid>
                          )}
                    </Grid>

                    {/* Show selected job details button */}
                    {selectedJob && (
                      <Box sx={{ mt: 3, textAlign: 'center' }} id="application-form">
                        <Collapse in={!!selectedJob}>
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => setJobDetailsOpen(true)}
                            startIcon={<InfoIcon />}
                            sx={{
                              borderColor: '#29204C',
                              color: '#29204C',
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 3,
                              py: 1,
                              '&:hover': {
                                borderColor: '#29204C',
                                background: '#f8f9fa',
                              },
                            }}
                          >
                            View Job Details for "{selectedJob.job_title}"
                          </Button>
                        </Collapse>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* ========== DOCUMENTS SECTION ========== */}
                  <Box sx={{ mb: 4 }}>
                    {/* Section Header */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: '#29204C',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CloudUpload sx={{ fontSize: 22, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" sx={{ color: '#29204C', fontWeight: 700 }}>
                        Upload Your CV
                      </Typography>
                    </Stack>

                    <Box
                      onClick={() => !cvFile && !cvUploading && cvInputRef.current?.click()}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, 'cv')}
                      sx={{
                        p: 2,
                        border: cvFile ? '1px solid #29204C' : '1px dashed rgba(0,0,0,0.23)',
                        borderRadius: 1,
                        cursor: cvFile || cvUploading ? 'default' : 'pointer',
                        transition: 'all 0.2s',
                        background: cvFile ? '#f8f7fa' : 'transparent',
                        '&:hover': !cvFile && !cvUploading ? {
                          borderColor: '#29204C',
                          background: '#fafafa',
                        } : {},
                      }}
                    >
                      <input
                        ref={cvInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleCvChange}
                        style={{ display: 'none' }}
                        disabled={cvUploading}
                      />

                      {cvUploading ? (
                        <Box>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <CircularProgress size={24} sx={{ color: '#29204C' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#29204C', mb: 0.5 }}>
                                Uploading CV...
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={cvUploadProgress}
                                sx={{
                                  height: 4,
                                  borderRadius: 2,
                                  backgroundColor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 2,
                                    background: '#29204C',
                                  },
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {cvUploadProgress}%
                            </Typography>
                          </Stack>
                        </Box>
                      ) : cvFile ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Description sx={{ fontSize: 28, color: '#29204C' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#29204C' }}>
                              {cvFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(cvFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCvFile(null);
                              setCvUploadProgress(0);
                            }}
                            sx={{ color: '#f44336' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <CloudUpload sx={{ fontSize: 28, color: '#29204C' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#29204C' }}>
                              {t.uploadCV}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              PDF, DOC, DOCX • Max 5MB
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* ========== COVER MESSAGE SECTION ========== */}
                  <Box sx={{ mb: 4 }}>
                    {/* Section Header */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: '#29204C',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ArticleIcon sx={{ fontSize: 22, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" sx={{ color: '#29204C', fontWeight: 700 }}>
                        Cover Message
                      </Typography>
                    </Stack>

                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      label={t.message}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={t.messagePlaceholder}
                      error={!!validationErrors.message}
                      helperText={validationErrors.message || `${formData.message.length} / 1000 ${t.characters}`}
                      inputProps={{ maxLength: 1000 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderWidth: 2, borderColor: 'rgba(0,0,0,0.12)' },
                          '&:hover fieldset': { borderColor: '#29204C' },
                        },
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* ========== PROFESSIONAL LINKS SECTION ========== */}
                  {selectedJob && selectedJob.requires_professional_info && (
                    <>
                      <Box sx={{ mb: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: '#29204C',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <LanguageIcon sx={{ fontSize: 22, color: 'white' }} />
                          </Box>
                          <Typography variant="h5" sx={{ color: '#29204C', fontWeight: 700 }}>
                            Professional Links
                          </Typography>
                        </Stack>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="LinkedIn Profile"
                              name="linkedin_url"
                              value={formData.linkedin_url}
                              onChange={handleInputChange}
                              placeholder="https://linkedin.com/in/your-profile"
                              error={!!validationErrors.linkedin_url}
                              helperText={validationErrors.linkedin_url}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LinkedInIcon sx={{ color: '#0077B5' }} />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Portfolio / Website"
                              name="portfolio_url"
                              value={formData.portfolio_url}
                              onChange={handleInputChange}
                              placeholder="https://your-portfolio.com"
                              error={!!validationErrors.portfolio_url}
                              helperText={validationErrors.portfolio_url}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <WebsiteIcon sx={{ color: '#29204C' }} />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="GitHub Profile"
                              name="github_url"
                              value={formData.github_url}
                              onChange={handleInputChange}
                              placeholder="https://github.com/your-username"
                              error={!!validationErrors.github_url}
                              helperText={validationErrors.github_url}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ my: 3 }} />

                      {/* ========== PROFESSIONAL INFORMATION SECTION ========== */}
                      <Box sx={{ mb: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: '#29204C',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <JobIcon sx={{ fontSize: 22, color: 'white' }} />
                          </Box>
                          <Typography variant="h5" sx={{ color: '#29204C', fontWeight: 700 }}>
                            Professional Information
                          </Typography>
                        </Stack>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ minWidth: '100%' }} error={!!validationErrors.years_of_experience}>
                              <InputLabel shrink>Years of Experience</InputLabel>
                              <Select
                                name="years_of_experience"
                                value={formData.years_of_experience}
                                onChange={handleInputChange}
                                displayEmpty
                                sx={{
                                  borderRadius: 2,
                                  width: '100%',
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                }}
                              >
                                <MenuItem value=""><Typography color="text.secondary">Please select...</Typography></MenuItem>
                                <MenuItem value="Fresh Graduate">Fresh Graduate</MenuItem>
                                <MenuItem value="Less than 1 year">Less than 1 year</MenuItem>
                                <MenuItem value="1-2 years">1-2 years</MenuItem>
                                <MenuItem value="3-5 years">3-5 years</MenuItem>
                                <MenuItem value="5-10 years">5-10 years</MenuItem>
                                <MenuItem value="10+ years">10+ years</MenuItem>
                              </Select>
                              {validationErrors.years_of_experience && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                  {validationErrors.years_of_experience}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ minWidth: '100%' }} error={!!validationErrors.education_level}>
                              <InputLabel shrink>Education Level</InputLabel>
                              <Select
                                name="education_level"
                                value={formData.education_level}
                                onChange={handleInputChange}
                                displayEmpty
                                sx={{
                                  borderRadius: 2,
                                  width: '100%',
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                }}
                              >
                                <MenuItem value=""><Typography color="text.secondary">Please select...</Typography></MenuItem>
                                <MenuItem value="High School">High School</MenuItem>
                                <MenuItem value="Associate Degree">Associate Degree</MenuItem>
                                <MenuItem value="Bachelor's Degree">Bachelor's Degree</MenuItem>
                                <MenuItem value="Master's Degree">Master's Degree</MenuItem>
                                <MenuItem value="PhD">PhD</MenuItem>
                              </Select>
                              {validationErrors.education_level && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                  {validationErrors.education_level}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Field of Study"
                              name="field_of_study"
                              value={formData.field_of_study}
                              onChange={handleInputChange}
                              placeholder="e.g., Computer Science"
                              error={!!validationErrors.field_of_study}
                              helperText={validationErrors.field_of_study}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="University / Institution"
                              name="university_name"
                              value={formData.university_name}
                              onChange={handleInputChange}
                              placeholder="e.g., Cairo University"
                              error={!!validationErrors.university_name}
                              helperText={validationErrors.university_name}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Graduation Year"
                              name="graduation_year"
                              type="number"
                              value={formData.graduation_year}
                              onChange={handleInputChange}
                              placeholder="e.g., 2020"
                              error={!!validationErrors.graduation_year}
                              helperText={validationErrors.graduation_year}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Current Job Title"
                              name="current_job_title"
                              value={formData.current_job_title}
                              onChange={handleInputChange}
                              placeholder="e.g., Software Engineer"
                              error={!!validationErrors.current_job_title}
                              helperText={validationErrors.current_job_title}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Current Company"
                              name="current_company"
                              value={formData.current_company}
                              onChange={handleInputChange}
                              placeholder="e.g., Tech Corp"
                              error={!!validationErrors.current_company}
                              helperText={validationErrors.current_company}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Expected Salary"
                              name="expected_salary"
                              value={formData.expected_salary}
                              onChange={handleInputChange}
                              placeholder="e.g., 15,000 EGP"
                              error={!!validationErrors.expected_salary}
                              helperText={validationErrors.expected_salary}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ minWidth: '100%' }} error={!!validationErrors.notice_period}>
                              <InputLabel shrink>Notice Period</InputLabel>
                              <Select
                                name="notice_period"
                                value={formData.notice_period}
                                onChange={handleInputChange}
                                displayEmpty
                                sx={{
                                  borderRadius: 2,
                                  width: '100%',
                                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)', borderWidth: 2 },
                                  '&:hover fieldset': { borderColor: '#29204C' },
                                }}
                              >
                                <MenuItem value=""><Typography color="text.secondary">Please select...</Typography></MenuItem>
                                <MenuItem value="Immediately">Immediately</MenuItem>
                                <MenuItem value="2 weeks">2 weeks</MenuItem>
                                <MenuItem value="1 month">1 month</MenuItem>
                                <MenuItem value="2 months">2 months</MenuItem>
                                <MenuItem value="3+ months">3+ months</MenuItem>
                              </Select>
                              {validationErrors.notice_period && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                  {validationErrors.notice_period}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ my: 3 }} />
                    </>
                  )}

                  {/* ========== SUBMIT SECTION ========== */}
                  <Box sx={{ mb: 0 }}>

                    <Box sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={consentAgreed}
                            onChange={(e) => setConsentAgreed(e.target.checked)}
                            sx={{
                              color: '#666',
                              '&.Mui-checked': {
                                color: '#29204C',
                              },
                            }}
                          />
                        }
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              {t.consentText}
                            </Typography>
                            <Tooltip title={t.viewPrivacyPolicy}>
                              <IconButton
                                size="small"
                                onClick={() => setConsentDialogOpen(true)}
                                sx={{ color: '#666' }}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        }
                        sx={{ mb: 0, alignItems: 'flex-start', ml: 0 }}
                      />
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={submitting || !consentAgreed}
                      startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                        borderRadius: 2,
                        textTransform: 'none',
                        minHeight: 56,
                        boxShadow: '0 4px 12px rgba(41, 32, 76, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1a1530 0%, #29204C 100%)',
                          boxShadow: '0 6px 16px rgba(41, 32, 76, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          background: '#e0e0e0',
                          color: '#9e9e9e',
                        },
                        transition: 'all 0.3s',
                      }}
                    >
                      {submitting ? t.submitting : t.submitApplication}
                    </Button>
                    {!consentAgreed && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                        Please agree to the data processing consent to submit your application
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Slide>


          {/* Enhanced Footer */}
          <Box sx={{ mt: 6, pb: 4 }}>
            <Divider sx={{ mb: 4 }} />
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#29204C', mb: 2 }}>
                  {t.companyName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t.companyDescription}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#29204C', mb: 2 }}>
                  {t.contactUs}
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <MailIcon sx={{ color: '#29204C' }} />
                    <Typography variant="body2" color="text.secondary">
                      info@z0n.net
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#29204C', mb: 2 }}>
                  {t.followUs}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/company/zon-net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      background: '#29204C',
                      color: 'white',
                      '&:hover': {
                        background: '#1a1530',
                      },
                    }}
                  >
                    <LinkedInIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.facebook.com/profile.php?id=61578063036876"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      background: '#29204C',
                      color: 'white',
                      '&:hover': {
                        background: '#1a1530',
                      },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setConsentDialogOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#29204C' }}
                  >
                    {t.privacyPolicy}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#29204C' }}
                  >
                    {t.termsConditions}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                {t.copyright}
              </Typography>
            </Box>
          </Box>
        </Container>

        {/* Job Details Dialog */}
        <Dialog
          open={jobDetailsOpen}
          onClose={() => setJobDetailsOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
            },
          }}
        >
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)', color: 'white' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={700}>
                {t.jobDetails}
              </Typography>
              <IconButton onClick={() => setJobDetailsOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedJob && (
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#29204C', mb: 3 }}>
                  {selectedJob.job_title}
                </Typography>

                {/* Description */}
                {selectedJob.job_description && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#29204C', mb: 1 }}>
                      {t.jobDescription}
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-line' }}>
                      {selectedJob.job_description}
                    </Typography>
                  </Box>
                )}

                {/* Requirements */}
                {selectedJob.requirements && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#29204C', mb: 1 }}>
                      Requirements
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-line' }}>
                      {selectedJob.requirements}
                    </Typography>
                  </Box>
                )}

                {/* Responsibilities */}
                {selectedJob.responsibilities && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#29204C', mb: 1 }}>
                      Responsibilities
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-line' }}>
                      {selectedJob.responsibilities}
                    </Typography>
                  </Box>
                )}

                {/* Additional Info */}
                {selectedJob.deadline && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Application Deadline
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#29204C' }}>
                      {new Date(selectedJob.deadline).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              variant="contained"
              onClick={() => setJobDetailsOpen(false)}
              sx={{
                px: 4,
                py: 1,
                background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              {t.close}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Consent Dialog */}
        <Dialog
          open={consentDialogOpen}
          onClose={() => setConsentDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
            },
          }}
        >
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)', color: 'white' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={700}>
                {t.privacyPolicyTitle}
              </Typography>
              <IconButton onClick={() => setConsentDialogOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#29204C', mb: 2 }}>
              {t.dataCollection}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 3, color: 'text.secondary' }}>
              {t.dataCollectionDesc}
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, color: 'text.secondary' }}>
                {t.personalContact}
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, color: 'text.secondary' }}>
                {t.professionalDocs}
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, color: 'text.secondary' }}>
                {t.applicationDetails}
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#29204C', mb: 2 }}>
              {t.yourRights}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
              {t.yourRightsDesc}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              variant="contained"
              onClick={() => setConsentDialogOpen(false)}
              sx={{
                px: 4,
                py: 1,
                background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              {t.iUnderstand}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{
              width: '100%',
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 3,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default PublicJobApplication;
